import {AddressResponseData, ICountriesLookup, IStateLookup} from "@connox/typescript-library";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import InfoIcon from "../../../lib/InfoIcon/InfoIcon";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {AddressType} from "./AddressFormData";
import {MaxLengthInput} from "../MaxLengthInput";

export interface IFormInfo {
  address: AddressResponseData;
  countries: ICountriesLookup[];
  selectedCountry?: string;
  selectedState?: string;
  states: IStateLookup[];
}

interface IAddressProps {
  addressType: AddressType;
  formRef: React.RefObject<HTMLFormElement>;
  formInfo: IFormInfo;
  onCountryChange: (evt: React.ChangeEvent<HTMLSelectElement>, addressType: AddressType) => void;
  onStateChange: (evt: React.ChangeEvent<HTMLSelectElement>, addressType: AddressType) => void;
}

const AddressForm: React.FC<IAddressProps> = (props) => {
  const {isCorporateCustomer, vatNumber} = useCheckoutContext().checkout.cartData;
  const {viewState} = useCartContext();
  const {t} = useTranslation("checkout");

  const {addressType, formRef} = props;
  const {address, countries, selectedCountry, selectedState, states} = props.formInfo;
  const isCorporate = viewState.partials ? viewState.partials.isCorporateCustomer : isCorporateCustomer;

  const allErrors: {[key: string]: string} = {};
  /*
   * if both true, user is in address step the first time, don't show errors
   * if submitted, partials will be set undefined, show errors
   * if logged in, both are false, show possible errors
   */
  if (!(viewState.partials && viewState.isRegistering)) {
    address.messages?.forEach((msg) => {
      allErrors[msg.field] = "invalid";
    });
  }

  const CountrySelect: React.FC = () => {
    if (!countries.length) {
      return null;
    }

    return (
      <div className="LongWidth">
        <select
          name="Country"
          className={allErrors.countryCode}
          onChange={(evt) => props.onCountryChange(evt, props.addressType)}
          value={selectedCountry || ""}
        >
          {/* Outputs all available Countries for delivery */}
          <option key="headline" value="" disabled={true}>
            {t(`Bitte wählen Sie ein ${addressType === AddressType.DELIVERY ? "Lieferland" : "Land"} *`)}
          </option>
          {availableCountries(countries)}
        </select>
        <span className="invalid-hint">{t("Bitte auswählen")}</span>
      </div>
    );
  };

  const zipMaxLength = (): number => {
    switch (selectedCountry) {
      case "AT":
      case "CH":
        return 4;
      case "DE":
        return 5;
      default:
        return 20;
    }
  };

  return (
    <form id={addressType} ref={formRef} action="#">
      <div className="InputContainer">
        <input type="hidden" name="Title" value={address.title || ""} />
        <input type="hidden" name="Gender" value={address.gender || "F"} />
        <input type="hidden" name="DateOfBirth" value={address.dateOfBirth || ""} />

        <FirstFourFields {...{address, allErrors, isCorporate}} />

        <PhoneNumberField {...{address, allErrors, isCorporate}} />

        <div className="Street ClearLeft">
          <MaxLengthInput
            className={allErrors.street}
            defaultValue={address.street}
            name="Street"
            placeholder={t("Straße *")}
            type="text"
          />
        </div>
        <div className="Number">
          <MaxLengthInput
            className={allErrors.number}
            defaultValue={address.number}
            maxLength={20}
            name="Number"
            placeholder={t("Nr. *")}
            type="text"
          />
        </div>
        <div className="Zip ClearLeft">
          <MaxLengthInput
            className={allErrors.zip}
            type="text"
            hintText={t("Bitte korrekt ausfüllen")}
            name="Zip"
            defaultValue={address.zip}
            placeholder={t("PLZ *")}
            maxLength={zipMaxLength()}
          />
        </div>
        <div className="City">
          <MaxLengthInput
            className={allErrors.city}
            type="text"
            name="City"
            defaultValue={address.city}
            placeholder={t("Stadt *")}
            maxLength={50}
          />
        </div>

        {addressType === AddressType.INVOICE && (!viewState.partials || allErrors.email) ? (
          <div className="LongWidth">
            <MaxLengthInput
              className={allErrors.email}
              type="email"
              name="Email"
              key="email"
              placeholder={t("Ihre E-Mail-Adresse *")}
              defaultValue={address.email || ""}
              maxLength={100}
            />
          </div>
        ) : (
          <input type="hidden" name="Email" value={address.email || ""} />
        )}

        <CountrySelect />

        {addressType === AddressType.INVOICE && isCorporate && (
          <div className="LongWidth">
            <MaxLengthInput type="text" name="VatNumber" defaultValue={vatNumber || ""} placeholder={t("USt.-ID")} />
          </div>
        )}
        {states.length > 0 ? (
          <div className="LongWidth">
            <select name="State" value={selectedState} onChange={(evt) => props.onStateChange(evt, props.addressType)}>
              {availableStates(states)}
            </select>
          </div>
        ) : (
          <input className="State" type="hidden" name="State" id="State" value={selectedState || ""} />
        )}
      </div>
    </form>
  );
};

export default AddressForm;

interface IFieldInfos {
  address: AddressResponseData;
  allErrors: {[key: string]: string};
  isCorporate: boolean;
}

const PhoneNumberField: React.FC<IFieldInfos> = (props) => {
  const {address, allErrors, isCorporate} = props;
  const {t} = useTranslation("checkout");

  if (!isCorporate && !allErrors.phone) {
    return <input type="hidden" name="PhoneNo" defaultValue={address.phone || undefined} />;
  }

  return (
    <div className="PhoneNo LongWidth">
      <InfoIcon style={{float: "right", height: "34px"}}>
        <p>{t("Ihre Telefonnummer benötigen wir für eventuelle Rückfragen ...")}</p>
      </InfoIcon>
      <MaxLengthInput
        className={allErrors.phone}
        defaultValue={address.phone || undefined}
        name="PhoneNo"
        placeholder={t("Telefonnummer")}
        type="tel"
        invalidHintCSSProps={{float: "left"}}
        lengthHintCSSProps={{float: "left"}}
      />
    </div>
  );
};

const FirstFourFields: React.FC<IFieldInfos> = (props) => {
  const {address, allErrors, isCorporate} = props;
  const {t} = useTranslation("checkout");

  return (
    <React.Fragment>
      {isCorporate ? (
        <div className="LongWidth">
          <MaxLengthInput
            className={allErrors.company}
            type="text"
            name="Company"
            defaultValue={address.company || undefined}
            placeholder={t("Firma")}
            maxLength={50}
          />
        </div>
      ) : (
        <input type="hidden" name="Company" defaultValue={address.company || undefined} />
      )}

      <div className="FirstName ClearLeft">
        <MaxLengthInput
          type="text"
          className={allErrors.firstName}
          name="FirstName"
          placeholder={t(isCorporate ? "z. Hd. Vorname *" : "Vorname *")}
          defaultValue={address.firstName}
          maxLength={50}
        />
      </div>

      <div className="LastName">
        <MaxLengthInput
          type="text"
          className={allErrors.lastName}
          name="LastName"
          placeholder={t(isCorporate ? "z. Hd. Nachname *" : "Nachname *")}
          defaultValue={address.lastName}
          maxLength={50}
        />
      </div>

      {isCorporate ? (
        <div className="LongWidth">
          <MaxLengthInput
            type="text"
            name="Department"
            placeholder={t("Abteilung")}
            defaultValue={address.department || undefined}
            maxLength={50}
          />
        </div>
      ) : (
        <input type="hidden" name="Department" defaultValue={address.department || undefined} />
      )}
    </React.Fragment>
  );
};

/**
 * options array over available countries
 */
const availableCountries = (countries: ICountriesLookup[]): JSX.Element[] =>
  countries
    .filter((country) => country.isDeliveryAllowed)
    .map((country) => {
      return (
        <option key={country.countryCode} value={country.countryCode} data-is-state-required={country.isStateRequired}>
          {country.name}
        </option>
      );
    });

/**
 * options array over available states
 */
const availableStates = (states: IStateLookup[]) =>
  states.map((federal) => {
    return (
      <option key={federal.stateCode} value={federal.stateCode}>
        {federal.name}
      </option>
    );
  });
